import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, CircleX, FileText, X } from "lucide-react";
import { Button } from "@/components/Button";
import { NormalButton } from "@/components/NormalButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/Avatar";
import { Card } from "@/components/Card";
import { Heading, Text } from "@/components/Typography";
import { FormattedText } from "@/components/FormattedText";
import { ProfileField } from "@/pages/profile/components/ProfileField";
import {
  CurrentRoleIcon,
  ExternalLinkIcon,
  FunctionIcon,
  IndustryIcon,
  LinkedInIcon,
  LocationIcon,
} from "@/components/icons/icons";
import { useMasterData } from "@/hooks/useMasterData";
import type { MasterDataItem } from "@/hooks/useMasterData";
import { MASTER_SCOPES } from "@/constants/masterScopes";
import type { MasterScope } from "@/constants/masterScopes";
import { api, SuggestedAdvisor } from "@/services/api";
import type { AdvisoryStyleItem, PublicAdvisorProfileResponse } from "@/services/publicApi";
import { cn } from "@/utils/cn";
import { getInitials, parseAdvisoryStyleOptions, parseMasterData } from "@/utils/util";
import { normalizeApprovalStatus } from "@/utils/pabUtils";

interface ViewAdvisorProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  advisor: SuggestedAdvisor | null;
  onInviteToPab?: () => void;
  /** Present only where booking a 1:1 with this person is offered — the
   *  Directory. Kept separate from onInviteToPab so a surface can show one
   *  without the other. */
  onRequestIntro?: () => void;
  approvalStatus?: string;
  publicProfile?: PublicAdvisorProfileResponse | null;
  renderMode?: "drawer" | "page";
  /**
   * `member` shows a non-expert (e.g. a program's achiever) with the same
   * drawer: profiles are unified per user, but the expert platform stats
   * (sessions, PABs, platform experience) mean nothing for them, so that row
   * and its request are skipped.
   */
  audience?: "expert" | "member";
}

type DrawerResolvedProfile = {
  currentTitle?: string;
  currentCompany?: string;
  location?: string;
  primaryFunction?: string;
  currentRoleLevel?: string;
  yearsOfExperience?: string;
  experienceContext?: string[];
  bio?: string;
  linkedIn?: string;
  personalWebsite?: string;
  profilePictureUrl?: string | null;
  email?: string;
  contactEmail?: string;
  advisoryStyles?: string[];
  coachingStrengths?: string[];
  levelsMentored?: string[];
};

interface SectionCardProps {
  title: string;
  children: React.ReactNode;
}

const SECTION_CARD_CLASS =
  "card-no-hover rounded-xl border border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card p-[25px] shadow-sm";
const SECTION_TITLE_CLASS =
  "font-inter font-bold text-18 leading-7 text-goals-create-title dark:text-white tracking-[-0.44px]";
const PILL_CLASS =
  "inline-flex items-center rounded-lg border px-[9px] py-[3px] text-[15px] leading-4 font-medium bg-[var(--color-profile-skill-pill-bg)] border-[var(--color-profile-skill-pill-border)] text-[var(--color-profile-skill-pill-text)] dark:bg-pab-dark-pill-bg dark:border-pab-dark-pill-border dark:text-pab-dark-pill-text font-inter text-15";

const normalizeExternalUrl = (value: string): string =>
  value.trim().startsWith("http") ? value.trim() : `https://${value.trim()}`;

const SectionCard = ({ title, children }: SectionCardProps) => (
  <Card className={SECTION_CARD_CLASS}>
    <Heading as="h3" className={cn(SECTION_TITLE_CLASS, "mb-4")}>
      {title}
    </Heading>
    {children}
  </Card>
);

export const ViewAdvisorProfileDrawer = ({
  isOpen,
  onClose,
  advisor,
  onInviteToPab,
  onRequestIntro,
  approvalStatus,
  publicProfile = null,
  audience = "expert",
  renderMode = "drawer",
}: ViewAdvisorProfileDrawerProps) => {
  const isPublicMode = Boolean(publicProfile);
  // v3: advisor.id is the user_id (SHA256 string) -- Number(advisor.id) here
  // used to send NaN into getProfile/getPlatformStats, breaking both calls.
  const advisorId = advisor?.id ?? null;
  const advisorName = publicProfile?.fullName ?? advisor?.name ?? "Expert";

  const {
    data: profile,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["advisorProfile", "drawer", advisorId],
    queryFn: () => api.advisors.getProfile(advisorId!),
    enabled: isOpen && !isPublicMode && advisorId != null,
  });

  const { data: platformStats } = useQuery({
    queryKey: ["advisorPlatformStats", "drawer", advisorId],
    queryFn: () => api.advisors.getPlatformStats(advisorId!),
    enabled: isOpen && !isPublicMode && audience === "expert" && advisorId != null,
    retry: false,
  });

  const advisoryStyleScope: MasterScope = isPublicMode
    ? ("" as MasterScope)
    : MASTER_SCOPES.ADVISORY_STYLE;
  const { data: advisoryStylesMasterData } = useMasterData<MasterDataItem>(advisoryStyleScope);

  const resolvedProfile: DrawerResolvedProfile | undefined = publicProfile
    ? {
        currentTitle: publicProfile.currentTitle,
        currentCompany: publicProfile.currentCompany,
        location: publicProfile.location,
        primaryFunction: publicProfile.primaryFunction,
        currentRoleLevel: publicProfile.currentRoleLevel,
        yearsOfExperience: publicProfile.yearsOfExperience,
        experienceContext: publicProfile.experienceContext,
        bio: publicProfile.bio,
        linkedIn: publicProfile.linkedIn,
        personalWebsite: publicProfile.personalWebsite,
        profilePictureUrl: publicProfile.profilePictureUrl,
        levelsMentored: publicProfile.levelsMentored,
        coachingStrengths: publicProfile.coachingStrengths,
      }
    : (profile as DrawerResolvedProfile | undefined);

  const parsedAdvisoryStylesAll = useMemo(() => {
    const options = parseMasterData(advisoryStylesMasterData ?? undefined);
    return parseAdvisoryStyleOptions(options);
  }, [advisoryStylesMasterData]);

  const advisoryStyleDisplayItems = useMemo(() => {
    const values: string[] = Array.isArray(resolvedProfile?.advisoryStyles)
      ? resolvedProfile.advisoryStyles
      : [];
    if (values.length === 0) return [];
    return parsedAdvisoryStylesAll.filter((style) => values.includes(style.value));
  }, [parsedAdvisoryStylesAll, resolvedProfile?.advisoryStyles]);

  const advisoryStyleFallbackRaw: string[] = useMemo(() => {
    if (advisoryStyleDisplayItems.length > 0) return [];
    if (
      Array.isArray(resolvedProfile?.advisoryStyles) &&
      resolvedProfile.advisoryStyles.length > 0
    ) {
      return resolvedProfile.advisoryStyles;
    }
    return [];
  }, [advisoryStyleDisplayItems.length, resolvedProfile?.advisoryStyles]);

  // Public mode: backend already resolves advisory styles to {title, description}
  const publicAdvisoryStyles: AdvisoryStyleItem[] = isPublicMode
    ? (publicProfile?.advisoryStyles ?? [])
    : [];

  const roleAndCompany =
    resolvedProfile?.currentTitle && resolvedProfile?.currentCompany
      ? `${resolvedProfile.currentTitle} @${resolvedProfile.currentCompany}`
      : [resolvedProfile?.currentTitle, resolvedProfile?.currentCompany]
          .filter(Boolean)
          .join(" @") || null;

  const orgAndExperience =
    [resolvedProfile?.currentRoleLevel, resolvedProfile?.yearsOfExperience]
      .filter(Boolean)
      .join(" · ") || null;

  const industryItems = resolvedProfile?.experienceContext?.length
    ? resolvedProfile.experienceContext
    : [];

  const topAreas = resolvedProfile?.coachingStrengths?.length
    ? resolvedProfile.coachingStrengths
    : [];
  const levelsMentored = resolvedProfile?.levelsMentored?.length
    ? resolvedProfile.levelsMentored
    : [];

  const socialLinkItems = [
    {
      key: "linkedin",
      label: "LinkedIn",
      value: resolvedProfile?.linkedIn ?? "",
      icon: LinkedInIcon,
      iconBg: "var(--color-profile-linked-in-icon-chip-bg)",
    },
    {
      key: "website",
      label: "Website / Portfolio",
      value: resolvedProfile?.personalWebsite ?? "",
      icon: IndustryIcon,
      iconBg: "var(--color-profile-icon-chip-bg)",
    },
  ] as const;

  const typedProfile = resolvedProfile as { email?: string; contactEmail?: string } | null;
  // Email is only shown in the internal advisor-matching drawer, never on public profiles.
  const profileEmail = isPublicMode
    ? ""
    : (typedProfile?.email ?? typedProfile?.contactEmail ?? "");

  const normalizedStatus = approvalStatus ? normalizeApprovalStatus(approvalStatus) : null;

  if (!isOpen) return null;

  const content = (
    <div className="hide-scrollbar flex-1 overflow-y-auto px-6 py-6 pb-24">
      {!advisorId && !isPublicMode ? (
        <Text as="p" className="text-sm text-gray-500 dark:text-pab-dark-subtle-text">
          {audience === "member" ? "Profile details are unavailable." : "Expert details are unavailable."}
        </Text>
      ) : isLoading && !isPublicMode ? (
        <Text as="p" className="text-sm text-gray-500 dark:text-pab-dark-subtle-text">
          Loading profile details...
        </Text>
      ) : (isError || !resolvedProfile) && !isPublicMode ? (
        <Text as="p" className="text-sm text-error-500">
          Failed to load expert profile.
        </Text>
      ) : (
        <div className="space-y-6">
          <Card className={SECTION_CARD_CLASS}>
            <div className="grid grid-cols-[240px_minmax(0,1fr)] gap-6 max-sm:grid-cols-1">
              <Avatar className="h-[280px] w-[240px] shrink-0 !rounded-[16px] border border-primary-100 dark:border-pab-dark-avatar-border bg-primary-50 dark:bg-surface-card max-sm:h-56 max-sm:w-full">
                <AvatarImage
                  src={resolvedProfile?.profilePictureUrl || advisor?.profileUrl || ""}
                  alt={advisorName}
                  className="h-full w-full object-cover grayscale"
                />
                <AvatarFallback className="!rounded-[16px] bg-primary-50 dark:bg-surface-card text-40 font-bold text-primary-700 dark:text-pab-dark-heading-text">
                  {getInitials(advisorName)}
                </AvatarFallback>
              </Avatar>

              <div className="min-w-0 overflow-hidden">
                <Text
                  as="p"
                  className="truncate font-inter text-[22px] font-bold leading-[33px] text-secondary-900 dark:text-white"
                >
                  {advisorName}
                </Text>
                {profileEmail ? (
                  <Text
                    as="p"
                    className="truncate font-instrument text-16 leading-7 text-secondary-500 dark:text-pab-dark-subtle-text"
                  >
                    {profileEmail}
                  </Text>
                ) : null}

                <div className="mt-4 space-y-3">
                  <ProfileField
                    icon={
                      <CurrentRoleIcon
                        className="text-neutral-600 dark:text-pab-dark-subtle-text"
                        size={16}
                      />
                    }
                    label="Current Role & Company"
                    value={
                      roleAndCompany ? (
                        <span>
                          {resolvedProfile?.currentTitle}{" "}
                          {resolvedProfile?.currentCompany ? (
                            <span className="text-[var(--color-profile-company-chip-bg)] underline">
                              @{resolvedProfile.currentCompany}
                            </span>
                          ) : null}
                        </span>
                      ) : null
                    }
                  />
                  <ProfileField
                    icon={
                      <FunctionIcon
                        className="text-neutral-600 dark:text-pab-dark-subtle-text"
                        size={16}
                      />
                    }
                    label="Primary Function"
                    value={resolvedProfile?.primaryFunction}
                  />
                  <ProfileField
                    icon={
                      <LocationIcon
                        className="text-neutral-600 dark:text-pab-dark-subtle-text"
                        size={16}
                      />
                    }
                    label="Location & Timezone"
                    value={resolvedProfile?.location}
                  />
                  <ProfileField
                    icon={
                      <IndustryIcon
                        className="text-neutral-600 dark:text-pab-dark-subtle-text"
                        size={16}
                      />
                    }
                    label="Org Type & Experience"
                    value={orgAndExperience}
                  />
                </div>
              </div>
            </div>
          </Card>

          {!isPublicMode && audience === "expert" && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5">
              <Card className="card-no-hover card-no-padding rounded-stat-card border border-secondary-200 dark:border-surface-border bg-white dark:bg-surface-card shadow-stat-card">
                <div className="flex h-[114px] flex-col gap-2 px-[17px] pb-[13px] pt-[17px]">
                  <Text
                    as="p"
                    className="font-inter text-24 font-bold leading-8 tracking-[0.07px] text-secondary-900 dark:text-white"
                  >
                    {platformStats ? String(platformStats.totalSessions).padStart(2, "0") : "—"}
                  </Text>
                  <Text
                    as="p"
                    className="font-inter text-12 leading-4 text-accent-250 dark:text-pab-dark-subtle-text"
                  >
                    Total Sessions
                  </Text>
                </div>
              </Card>
              <Card className="card-no-hover card-no-padding rounded-stat-card border border-secondary-200 dark:border-surface-border bg-white dark:bg-surface-card shadow-stat-card">
                <div className="flex h-[114px] flex-col gap-2 px-[17px] pb-[13px] pt-[17px]">
                  <Text
                    as="p"
                    className="font-inter text-24 font-bold leading-8 tracking-[0.07px] text-secondary-900 dark:text-white"
                  >
                    {platformStats ? String(platformStats.acceptedPabs).padStart(2, "0") : "—"}
                  </Text>
                  <Text
                    as="p"
                    className="font-inter text-12 leading-4 text-accent-250 dark:text-pab-dark-subtle-text"
                  >
                    Achievers Helped
                  </Text>
                </div>
              </Card>
              <Card className="card-no-hover card-no-padding rounded-stat-card border border-secondary-200 dark:border-surface-border bg-white dark:bg-surface-card shadow-stat-card">
                <div className="flex h-[114px] flex-col gap-2 px-[17px] pb-[13px] pt-[17px]">
                  <Text
                    as="p"
                    className="font-inter text-24 font-bold leading-8 tracking-[0.07px] text-secondary-900 dark:text-white"
                  >
                    {"< 24 hours"}
                  </Text>
                  <Text
                    as="p"
                    className="font-inter text-12 leading-4 text-accent-250 dark:text-pab-dark-subtle-text"
                  >
                    Response Time
                  </Text>
                </div>
              </Card>
              <Card className="card-no-hover card-no-padding rounded-stat-card border border-secondary-200 dark:border-surface-border bg-white dark:bg-surface-card shadow-stat-card">
                <div className="flex h-[114px] flex-col gap-2 px-[17px] pb-[13px] pt-[17px]">
                  <Text
                    as="p"
                    className="font-inter text-24 font-bold leading-8 tracking-[0.07px] text-secondary-900 dark:text-white"
                  >
                    {platformStats
                      ? platformStats.platformExperience < 12
                        ? "< 1 year"
                        : `${String(Math.floor(platformStats.platformExperience / 12)).padStart(2, "0")} year${Math.floor(platformStats.platformExperience / 12) === 1 ? "" : "s"}`
                      : "—"}
                  </Text>
                  <Text
                    as="p"
                    className="font-inter text-12 leading-4 text-accent-250 dark:text-pab-dark-subtle-text"
                  >
                    Years on Platform
                  </Text>
                </div>
              </Card>
            </div>
          )}

          {resolvedProfile?.bio && (
            <Card className={SECTION_CARD_CLASS}>
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-goals-purple-selected" />
                <Heading
                  as="h3"
                  className="font-inter text-18 font-bold leading-7 text-goals-create-title dark:text-white"
                >
                  Bio
                </Heading>
              </div>
              <Text className="font-inter text-[15px] leading-[22px] text-goals-create-title dark:text-pab-dark-subtle-text whitespace-pre-line">
                <FormattedText text={resolvedProfile.bio} />
              </Text>
            </Card>
          )}

          {industryItems.length > 0 && (
            <SectionCard title="Industry Experience">
              <div className="flex flex-wrap gap-x-5 gap-y-3.5">
                {industryItems.map((item, index) => (
                  <span key={`${item}-${index}`} className={PILL_CLASS}>
                    {item}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}

          {isPublicMode
            ? publicAdvisoryStyles.length > 0 && (
                <SectionCard title="Advisory Style">
                  <div className="grid grid-cols-2 gap-3">
                    {publicAdvisoryStyles.map((style, index) => (
                      <div
                        key={`${style.title}-${index}`}
                        className="rounded-lg border border-profile-skill-pill-border dark:border-surface-border bg-profile-skill-pill-bg dark:bg-surface-card-inner px-[17px] py-[15px]"
                      >
                        <Text
                          as="div"
                          className="font-inter text-16 font-semibold leading-6 tracking-[-0.31px] text-profile-skill-pill-text dark:text-pab-dark-heading-text"
                        >
                          {style.title}
                        </Text>
                        {style.description ? (
                          <Text
                            as="p"
                            className="mt-2 font-inter text-14 font-normal leading-advisor-bio tracking-advisor-bio text-ai-goal-back-btn-text dark:text-pab-dark-subtle-text"
                          >
                            {style.description}
                          </Text>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )
            : (advisoryStyleDisplayItems.length > 0 || advisoryStyleFallbackRaw.length > 0) && (
                <SectionCard title="Advisory Style">
                  {advisoryStyleDisplayItems.length > 0 ? (
                    <div className="space-y-3">
                      {advisoryStyleDisplayItems.slice(0, 2).map((style) => (
                        <div
                          key={style.value}
                          className="rounded-lg border border-profile-skill-pill-border dark:border-surface-border bg-profile-skill-pill-bg dark:bg-surface-card-inner px-[17px] py-[15px]"
                        >
                          <Text
                            as="div"
                            className="font-inter text-16 font-semibold leading-6 tracking-[-0.31px] text-profile-skill-pill-text dark:text-pab-dark-heading-text"
                          >
                            {style.title}
                          </Text>
                          {style.description ? (
                            <Text
                              as="p"
                              className="mt-2 font-inter text-14 font-normal leading-advisor-bio tracking-advisor-bio text-ai-goal-back-btn-text dark:text-pab-dark-subtle-text"
                            >
                              {style.description}
                            </Text>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {advisoryStyleFallbackRaw.slice(0, 2).map((item, index) => (
                        <div
                          key={`${item}-${index}`}
                          className="rounded-lg border border-profile-skill-pill-border dark:border-surface-border bg-profile-skill-pill-bg dark:bg-surface-card-inner px-[17px] py-[15px]"
                        >
                          <Text
                            as="div"
                            className="font-inter text-16 font-semibold leading-6 tracking-[-0.31px] text-profile-skill-pill-text dark:text-pab-dark-heading-text"
                          >
                            {item}
                          </Text>
                          <Text
                            as="p"
                            className="mt-2 font-inter text-14 font-normal leading-advisor-bio tracking-advisor-bio text-ai-goal-back-btn-text dark:text-pab-dark-subtle-text"
                          >
                            Expert style and experience in this area.
                          </Text>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
              )}

          {levelsMentored.length > 0 && (
            <SectionCard title="Levels Advised">
              <div className="flex flex-wrap gap-x-5 gap-y-3.5">
                {levelsMentored.map((level: string, index: number) => (
                  <span key={`${level}-${index}`} className={PILL_CLASS}>
                    {level}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}

          {topAreas.length > 0 && (
            <SectionCard title="Top Areas">
              <div className="flex flex-wrap gap-x-5 gap-y-3.5">
                {topAreas.map((area: string, index: number) => (
                  <span key={`${area}-${index}`} className={PILL_CLASS}>
                    {area}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard title="Social & Links">
            <div className="space-y-3">
              {socialLinkItems.map((item) => {
                const hasValue = Boolean(item.value?.trim());
                const content = (
                  <>
                    <div
                      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
                      style={{ backgroundColor: item.iconBg }}
                    >
                      <item.icon className="h-4 w-4 text-primary-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Text
                        as="p"
                        className="font-inter text-14 font-semibold text-goals-create-title dark:text-white"
                      >
                        {item.label}
                      </Text>
                      <Text
                        as="p"
                        className="truncate font-instrument text-14 font-normal leading-5 text-ai-goal-back-btn-text dark:text-pab-dark-subtle-text"
                      >
                        {hasValue ? item.value : "No link yet"}
                      </Text>
                    </div>
                    {hasValue ? (
                      <ExternalLinkIcon className="h-4 w-4 flex-shrink-0 text-neutral-400 dark:text-pab-dark-subtle-text" />
                    ) : null}
                  </>
                );

                if (hasValue) {
                  return (
                    <a
                      key={item.key}
                      href={normalizeExternalUrl(item.value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-xl border border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card-inner p-3 transition-colors hover:bg-neutral-50 dark:hover:bg-surface-card"
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <div
                    key={item.key}
                    className="flex items-center gap-3 rounded-xl border border-dashed border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card-inner p-3 text-neutral-500 dark:text-pab-dark-subtle-text"
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );

  if (renderMode === "page") {
    return (
      <div className="w-full bg-white dark:bg-surface-page-bg">
        <div className="flex flex-col font-inter">{content}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div
        className="absolute right-0 top-0 h-full w-full max-w-[750px] overflow-hidden bg-secondary-100 dark:bg-surface-page-bg shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="view-advisor-profile-title"
      >
        <div className="flex h-full flex-col font-inter">
          <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card px-6 py-4">
            <Heading
              as="h2"
              className="font-inter text-20 font-bold leading-7 tracking-[-0.45px] text-goals-create-title dark:text-white"
            >
              Profile Details
            </Heading>
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] p-2 text-secondary-500 dark:text-pab-dark-subtle-text transition-colors hover:bg-secondary-100 dark:hover:bg-surface-card-inner"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {content}
          {/* Request Intro — a separate footer from the PAB one below.
              The Directory deliberately omits `onInviteToPab` to get a
              read-only drawer, so hanging this off that prop would have made
              the two affordances impossible to have independently. */}
          {onRequestIntro !== undefined && (
            <div className="shrink-0 border-t border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card px-6 py-4">
              <Button
                variant="primaryGradient"
                className="w-full h-11 text-14 font-semibold font-inter text-white"
                onClick={onRequestIntro}
              >
                Request Intro
              </Button>
            </div>
          )}
          {onInviteToPab !== undefined && (
            <div className="shrink-0 border-t border-neutral-200 dark:border-surface-border bg-white dark:bg-surface-card px-6 py-4">
              {normalizedStatus === "accepted" ? (
                <NormalButton
                  borderColor="border-status-success-border"
                  bgColor="bg-status-success-bg"
                  textColor="text-status-success-text"
                  hoverBgColor=""
                  leftIcon={<CircleCheck className="h-4 w-4 text-status-success-text" />}
                  className="w-full h-11 text-14 font-semibold font-inter pointer-events-none"
                >
                  Accepted
                </NormalButton>
              ) : normalizedStatus === "rejected" ? (
                <NormalButton
                  borderColor="border-error-200"
                  bgColor="bg-error-50"
                  textColor="text-error-600"
                  hoverBgColor=""
                  leftIcon={<CircleX className="h-4 w-4 text-error-600" />}
                  className="w-full h-11 text-14 font-semibold font-inter pointer-events-none"
                >
                  Rejected
                </NormalButton>
              ) : normalizedStatus === "invited" ? (
                <NormalButton
                  borderColor="border-status-success-border"
                  bgColor="bg-status-success-bg"
                  textColor="text-status-success-text"
                  hoverBgColor=""
                  leftIcon={<CircleCheck className="h-4 w-4 text-status-success-text" />}
                  className="w-full h-11 text-14 font-semibold font-inter pointer-events-none"
                >
                  Invited
                </NormalButton>
              ) : (
                <Button
                  variant="primaryGradient"
                  className="w-full h-11 text-14 font-semibold font-inter text-white"
                  onClick={onInviteToPab}
                >
                  Invite to Advisory Board
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ViewAdvisorProfileDrawer.displayName = "ViewAdvisorProfileDrawer";

export default ViewAdvisorProfileDrawer;
